import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tooltip,
  Button,
  ButtonGroup,
  Flex,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Center
} from "@chakra-ui/react";
import { GrView } from "react-icons/gr";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { getAllSubAnchors } from "services/Wallet/subAnchorService";
import { useWallet } from "contexts/WalletContext";
import { getPageItems } from '../utils/pagination';

const SubAnchorTable = ({ searchQuery, refreshTrigger }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setWallet } = useWallet();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 5;
  const fetchInFlightRef = useRef(null);

  const fetchSubAnchors = useCallback(async (page = 1) => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { items: data, pagination } = await getAllSubAnchors(id, page, itemsPerPage);

      const enrichedData = data.map((item) => ({
        id: item?.id,
        name: item?.dsa_code,
        balance: item?.amount,
        lasttopup: item?.last_topup ?? "N/A",
        status: item?.amount <= item?.minimum_amount ? "Low" : "Active",
        walletId: item?.wallet_id,
        mobile: item?.mobile,
        email: item?.email ?? "",
      }));

      setItems(enrichedData);
      setTotalPages(Math.ceil(pagination.total / itemsPerPage));
      setTotalItems(pagination.total);
      
    } catch (error) {
      console.error("Error fetching sub anchors:", error);
      setError("Failed to load DSA data. Please try again.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [id, itemsPerPage]);

  useEffect(() => {
    if (!id) return;
    const requestKey = `${id}-${currentPage}-${itemsPerPage}-${refreshTrigger ?? 0}`;
    if (fetchInFlightRef.current === requestKey) return;
    fetchInFlightRef.current = requestKey;
    fetchSubAnchors(currentPage).finally(() => {
      fetchInFlightRef.current = null;
    });
  }, [id, currentPage, fetchSubAnchors, refreshTrigger, itemsPerPage]);

  const handleView = (dsaId, dsaCode, walletId) => {
    setWallet(prev => ({
      ...prev,
      dsaCode: dsaCode,
      dsaId: dsaId,
      subAnchorWalletId: walletId
    }));
    navigate(`/partner-detail/${id}/${dsaCode}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Filter items based on search query (client-side filtering for current page)
  const filteredItems = items.filter((item) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(q) ||
      String(item.balance).includes(q) ||
      item.status.toLowerCase().includes(q)
    );
  });

  const formatDate = (dateString) => {
    if (dateString === "N/A" || !dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (error) {
      return "N/A";
    }
  };

  if (error) {
    return (
      <Alert status="error" mb={4}>
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <>
      <TableContainer>
        <Table variant="outline" size="md">
          <Thead>
            <Tr bg="#F9FAFB" className="font-semibold text-black">
              <Th>DSA</Th>
              <Th>Wallet Balance</Th>
              <Th>Last Topup</Th>
              <Th>Status</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody className="text-sm">
            {loading ? (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  <Center my={8}>
                    <Spinner size="md" />
                  </Center>
                </Td>
              </Tr>
            ) : filteredItems.length === 0 ? (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  <Text py={8} color="gray.500">
                    {searchQuery ? "No DSA found matching your search" : "No DSA found"}
                  </Text>
                </Td>
              </Tr>
            ) : (
              filteredItems.map((item) => (
                <Tr key={item.id}>
                  <Td fontWeight="medium">{item.name}</Td>
                  <Td isNumeric fontWeight="medium">
                    ₹{item.balance?.toFixed(2)}
                  </Td>
                  <Td>{formatDate(item.lasttopup)}</Td>
                  <Td>
                    <Text 
                      color={item.status === "Low" ? "red.500" : "green.500"}
                      fontWeight="medium"
                    >
                      {item.status}
                    </Text>
                  </Td>
                  <Td>
                    <Tooltip label="View Details">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleView(item.id, item.name, item.walletId)}
                      >
                        <GrView />
                      </Button>
                    </Tooltip>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <Flex justify="space-between" align="center" mt={4}>
          <Text color="gray.600">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} DSA
          </Text>
          <ButtonGroup spacing={2}>
            <Button
              size="sm"
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              isDisabled={currentPage === 1}
            >
              Prev
            </Button>
            
            {getPageItems(currentPage, totalPages, 1).map((item, idx) =>
              item === 'ellipsis' ? (
                <Button key={`e-${idx}`} size="sm" isDisabled variant="ghost">
                  …
                </Button>
              ) : (
                <Button
                  key={item}
                  size="sm"
                  variant={currentPage === item ? "solid" : "outline"}
                  onClick={() => handlePageChange(item)}
                >
                  {item}
                </Button>
              )
            )}

            <Button
              size="sm"
              onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </ButtonGroup>
        </Flex>
      )}
    </>
  );
};

export default SubAnchorTable;
